import { useState, useEffect } from "react";
import api, { formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { MessageCircle, Copy, PlugZap, Send, CheckCircle2, XCircle, Loader2 } from "lucide-react";

const Dot = ({ ok }) => ok
  ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
  : <XCircle className="w-4 h-4 text-destructive shrink-0" />;

const Row = ({ label, ok, testid }) => (
  <div className="flex items-center justify-between gap-2 py-1.5 border-b border-border/50 last:border-0" data-testid={testid}>
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="flex items-center gap-1.5 text-sm font-medium">{ok ? "SÌ" : "NO"} <Dot ok={ok} /></span>
  </div>
);

export default function WhatsAppCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");
  const [to, setTo] = useState("");
  const [msg, setMsg] = useState("Test Dogana WhatsApp API");

  const load = async () => { try { const { data } = await api.get("/whatsapp/status"); setSt(data); } catch { /* noop */ } };
  useEffect(() => { load(); }, []);

  const testConn = async () => {
    setBusy("conn");
    try { const { data } = await api.post("/whatsapp/test-connection"); toast.success(`Connessione OK · ${data.verified_name || ""} ${data.display_phone_number || ""}`.trim()); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const sendTest = async () => {
    if (!to.trim()) { toast.error("Inserisci un numero in formato E.164 (+39...)"); return; }
    setBusy("msg");
    try { const { data } = await api.post("/whatsapp/test-message", { to: to.trim(), message: msg }); toast.success(`Messaggio inviato · ID ${data.message_id || "accettato"}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const copyUrl = async () => { try { await navigator.clipboard.writeText(st?.webhook_url || ""); toast.success("URL webhook copiato"); } catch { toast.error("Copia non riuscita"); } };

  return (
    <Card className="p-6 rounded-xl max-w-2xl mb-6" data-testid="whatsapp-card">
      <div className="flex items-center gap-2 mb-1">
        <MessageCircle className="w-5 h-5 text-primary" />
        <h2 className="font-dash font-semibold text-lg">WhatsApp (Meta Cloud API)</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Integrazione con il numero WhatsApp Business del locale. Le credenziali restano sul server e non sono mai mostrate qui.</p>

      {!st ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento stato…</div>
      ) : (
        <>
          <div className="rounded-xl border border-border p-3 mb-4">
            <Row label="Token configurato" ok={st.token_configured} testid="wa-row-token" />
            <Row label="Phone Number ID" ok={st.phone_number_id_configured} testid="wa-row-phone" />
            <Row label="WABA ID" ok={st.waba_id_configured} testid="wa-row-waba" />
            <Row label="Verify Token" ok={st.verify_token_configured} testid="wa-row-verify" />
            <Row label="Webhook verificato" ok={st.webhook_verified} testid="wa-row-webhook" />
            <div className="flex items-center justify-between gap-2 py-1.5 text-xs text-muted-foreground">
              <span>Graph API</span><span className="font-mono">{st.graph_version}</span>
            </div>
          </div>

          <div className="rounded-xl border border-border p-3 mb-4">
            <Label className="text-xs text-muted-foreground">URL Webhook (da incollare in Meta)</Label>
            <div className="flex items-center gap-2 mt-1">
              <code className="flex-1 text-xs bg-secondary rounded px-2 py-1.5 truncate" data-testid="wa-webhook-url">{st.webhook_url}</code>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={copyUrl} data-testid="wa-copy-webhook"><Copy className="w-4 h-4" /></Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">Il Verify Token è salvato nelle variabili d'ambiente del server (<span className="font-mono">WHATSAPP_VERIFY_TOKEN</span>), non viene mostrato qui.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs mb-4">
            <div className="rounded-lg bg-secondary/50 p-2"><p className="text-muted-foreground">Ultimo webhook</p><p className="font-medium truncate" data-testid="wa-last-webhook">{st.last_webhook_at ? new Date(st.last_webhook_at).toLocaleString("it-IT") : "—"}</p></div>
            <div className="rounded-lg bg-secondary/50 p-2"><p className="text-muted-foreground">Ultimo invio</p><p className="font-medium truncate" data-testid="wa-last-sent">{st.last_sent?.at ? new Date(st.last_sent.at).toLocaleString("it-IT") : "—"}</p></div>
            <div className="rounded-lg bg-secondary/50 p-2"><p className="text-muted-foreground">Ultimo errore</p><p className="font-medium truncate text-destructive" data-testid="wa-last-error">{st.last_error ? (st.last_error_at ? new Date(st.last_error_at).toLocaleString("it-IT") : "sì") : "—"}</p></div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <Button onClick={testConn} disabled={busy === "conn" || !st.token_configured} className="rounded-full" data-testid="wa-test-connection">
              {busy === "conn" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PlugZap className="w-4 h-4 mr-1" /> Test connessione</>}
            </Button>
            <Button variant="outline" onClick={copyUrl} className="rounded-full" data-testid="wa-copy-webhook-2"><Copy className="w-4 h-4 mr-1" /> Copia URL webhook</Button>
          </div>

          <div className="rounded-xl border border-border p-3">
            <Label className="text-sm font-medium">Invia messaggio di test</Label>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-2 mt-2">
              <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="+39333..." data-testid="wa-test-to" />
              <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Messaggio" data-testid="wa-test-message" />
            </div>
            <Button onClick={sendTest} disabled={busy === "msg" || !st.token_configured} className="rounded-full mt-2 w-full sm:w-auto" data-testid="wa-send-test">
              {busy === "msg" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Invia messaggio test</>}
            </Button>
            {!st.token_configured && <p className="text-xs text-destructive mt-2">Integrazione non ancora configurata: aggiungi le credenziali Meta sul server per abilitare l'invio.</p>}
          </div>
        </>
      )}
    </Card>
  );
}
